import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Box, Card, CardHeader, HStack, Icon, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";
import moment from "moment";
import { FiCalendar } from "react-icons/fi";

import { selectActiveProperties } from "../../../redux/user/userSlice";
import { getPropertyAddress } from "../../../utils/verifier";

import PropertyFinancialStatementsCard from "./PropertyFinancialStatementsCard";

export default function FinancialStatements() {
	const textColor = useColorModeValue("gray.700", "white");
	const properties = useSelector(selectActiveProperties);
	const [selectedYear, setSelectedYear] = useState(moment().year());
	const { t } = useTranslation();

	// Get all financial statements from all properties
	const allFinancialStatements = properties
		.map((property) => {
			const address = getPropertyAddress(property);
			return (property.statements || []).map((statement) => {
				return { ...statement, address, propertyId: property.id };
			});
		})
		.flat();

	// Get unique years from all statements
	const years = [...new Set(allFinancialStatements.map((statement) => statement.year))];
	// Add current year if not in the list
	if (!years.includes(moment().year())) {
		years.push(moment().year());
	}
	// Sort years in descending order
	years.sort((a, b) => b - a);

	// Create options for ChakraSelect
	const yearOptions = years.map((year) => ({
		value: year,
		label: year.toString(),
	}));

	return (
		<Stack direction={"column"} spacing={4}>
			<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
				<CardHeader>
					<Text fontSize="xl" color={textColor} fontWeight="bold">
						{t("financialStatements.title")}
					</Text>
				</CardHeader>
			</Card>
			<Card p={4} boxShadow="sm" borderRadius="lg">
				<HStack spacing={3} align="center">
					<Icon as={FiCalendar} color="blue.500" boxSize={5} />
					<Text fontWeight="medium" fontSize="md">
						{t("table.year")}
					</Text>
					<Box width="200px">
						<ChakraSelect
							value={{ value: selectedYear, label: selectedYear.toString() }}
							onChange={(option) => setSelectedYear(option.value)}
							options={yearOptions}
							size="md"
							chakraStyles={{
								dropdownIndicator: (provided) => ({
									...provided,
									bg: "transparent",
									px: 2,
									cursor: "pointer",
								}),
								control: (provided) => ({
									...provided,
									borderRadius: "md",
									borderColor: "gray.300",
									_hover: { borderColor: "blue.500" },
								}),
								option: (provided, state) => ({
									...provided,
									bg: state.isSelected ? "blue.500" : state.isFocused ? "blue.50" : undefined,
									_hover: { bg: state.isSelected ? "blue.500" : "blue.50" },
								}),
							}}
						/>
					</Box>
				</HStack>
			</Card>
			{properties.map((property) => {
				const address = getPropertyAddress(property);
				const statements = (property.statements || [])
					.filter((statement) => statement.year === selectedYear)
					.map((statement) => {
						return { ...statement, address, propertyId: property.id };
					});

				return <PropertyFinancialStatementsCard key={property.id} statements={statements} address={address} propertyId={property.id} />;
			})}
		</Stack>
	);
}
